/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react'
import styled from 'styled-components'
import {AkChevronDownIcon} from '@fuegokit/fuegoicons-react'
import {Box, themeGet, VisuallyHidden, Text, useFilter} from '../..'
import Combobox from '../../components/Combobox'

import {ComboboxItemProps, ComboboxRootProps} from '../../components/Combobox/types'

export interface ActionsFilterProps<T extends object> extends ComboboxRootProps<T> {
  /**
   *
   */
  label?: string
  /**
   * The list of items to pass in to the Actions Filter.
   */
  items: Iterable<T>
  /**
   * The filter function used to determine if a option should be included in the Actions Filter list.
   */
  defaultFilter?: (textValue: string, inputValue: string) => boolean
  /**
   *
   */
  children: React.ReactNode
  /**
   * Additional helper text to associate with the Actions Filter
   */
  description?: string
  /**
   * Message to help a user fix a validation error.
   */
  errorMessage?: string
  /**
   * Whether to display the count of option types in the Actions Filter list
   */
  displayCount?: boolean
}

const ActionsFilter = <T extends object>({
  children,
  description,
  errorMessage,
  defaultFilter,
  label,
  items,
  displayCount = false,
  ...props
}: ActionsFilterProps<T>) => {
  // iterables aren't mappable
  const copiedItems = [...items]

  const {startsWith} = useFilter({sensitivity: 'base'})
  const [filterValue, setFilterValue] = React.useState('')

  const filteredItems = React.useMemo(
    () => copiedItems.filter((item: any) => startsWith(item.name, filterValue)),
    [items, filterValue]
  )

  // Store input value, selected option, open state, and items
  // in a state tracker
  const [fieldState, setFieldState] = React.useState({
    selectedKey: '' as React.Key,
    inputValue: '',
    items: copiedItems
  })

  return (
    <>
      <StyledActionsFilter
        label={label}
        defaultItems={copiedItems}
        inputValue={filterValue}
        onInputChange={setFilterValue}
        allowsCustomValue
      >
        <Combobox.Label>{label}</Combobox.Label>
        <Box
          sx={{
            display: 'flex'
          }}
        >
          <StyledActionsFilterInput />
          <StyledActionsFilterButton>
            <VisuallyHidden>Menu</VisuallyHidden>
            <AkChevronDownIcon />
          </StyledActionsFilterButton>
        </Box>
        {description && <StyledActionsFilterText slot="description">{description}</StyledActionsFilterText>}
        {errorMessage && <StyledActionsFilterText slot="errorMessage">{errorMessage}</StyledActionsFilterText>}
        <StyledActionsFilterPopover>
          <StyledActionsFilterPopover>
            <StyledActionsFilterListbox>
              {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
              {(item: any) => (
                <StyledComplexActionsFilterItem
                  textValue={item.displayName}
                  label={item.name}
                  leadingVisual={item.icon}
                >
                  <Box sx={{display: 'flex', gap: 1}}>
                    <Text sx={{fontWeight: 'medium'}}>{item.displayName}</Text>
                    {displayCount && <Text>({item.count})</Text>}
                  </Box>
                </StyledComplexActionsFilterItem>
              )}
            </StyledActionsFilterListbox>
          </StyledActionsFilterPopover>
        </StyledActionsFilterPopover>
      </StyledActionsFilter>
    </>
  )
}

export default ActionsFilter

export const fontSize = () => 14

export const CustomItem = ({UNSAFE_className, ...props}: ComboboxItemProps) => {
  return <Combobox.Item className={UNSAFE_className} {...props} />
}
export const StyledActionsFilter = styled(Combobox)`
  [slot='description'] {
    font-size: 12px;
  }

  [slot='errorMessage'] {
    font-size: 12px;
    color: var(--ds-text-danger);
  }
`
export const StyledActionsFilterItem = styled(CustomItem)<ComboboxItemProps>`
  padding: ${themeGet('space.2')} 0;
  /* border-radius: ${themeGet('radii.1')}; */
  outline: none;
  cursor: default;
  color: ${themeGet('colors.text.default')};
  font-size: ${themeGet('fontSizes.3')};
  position: relative;
  width: 100%;
  padding: 6px 12px 6px 16px;
  &:focus {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &:hover {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[data-focus-visible] {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-selected='true'] {
    background-color: ${themeGet('colors.background.selected.default.hovered')};
    color: ${themeGet('colors.text.selected')};
    font-weight: 600;
    //
    box-shadow: inset 2px 0px 0px ${themeGet('colors.border.selected')};

    &:hover {
      background: ${themeGet('colors.background.neutral.subtle.hovered')};
      color: ${themeGet('colors.text.default')};
    }
    &::before {
      /* content: '✓';
      content: '✓' / ''; */
      alt: ' ';
      position: absolute;
      top: 6px;
      left: 2px;
    }
  }
  &[data-pressed] {
    background-color: ${themeGet('colors.background.neutral.subtle.pressed')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-disabled] {
    background-color: ${themeGet('colors.elevation.surface.default.[default]')};
    color: ${themeGet('colors.text.disabled')};
    cursor: not-allowed;
  }

  @media (forced-colors: active) {
    &:focus {
      background: Highlight;
      color: HighlightText;
    }
  }
`
export const StyledActionsFilterPopover = styled(Combobox.Popover)`
  --background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  --border-color: ${themeGet('colors.border.default')};

  border: 1px solid var(--border-color);
  width: var(--trigger-width);
  box-sizing: border-box;
  box-shadow: ${themeGet('shadows.elevation.shadow.overlay')};
  background: var(--background-color);
  outline: none;
  border-radius: ${themeGet('radii.1')};
`
export const StyledActionsFilterListbox = styled(Combobox.Listbox)`
  --highlight-background: $themeGet('colors.background.neutral.subtle.hovered');
  --highlight-foreground: ${themeGet('colors.text.inverse')};
  --text-color: ${themeGet('colors.text.default')};
  --text-color-disabled: ${themeGet('colors.text.disabled')};

  max-height: inherit;
  box-sizing: border-box;
  overflow: auto;
  outline: none;
  padding: ${themeGet('space.2')} 0;
`
export const StyledActionsFilterInput = styled(Combobox.Input)`
  vertical-align: middle;
  //
  line-height: ${(8 * 2.5) / fontSize()};
  //
  align-items: center;
  border-radius: ${themeGet('radii.1')};
  border-width: 2;
  border-style: solid;
  display: flex;
  flex: 1;
  width: 100%;
  font-size: ${themeGet('fontSizes.3')};
  justify-content: space-between;
  //
  overflow: hidden;
  transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out;
  pointer-events: auto;
  //
  padding: ${themeGet('space.2')} 12px;
  //
  cursor: text;
  background-color: ${themeGet('colors.background.neutral.default.[default]')};
  border-color: transparent;
  color: inherit;
  //
  &[aria-invalid] {
    border-color: ${themeGet('colors.border.danger')};
  }

  &:focus {
    outline: none;
    border-color: ${themeGet('colors.border.focused')};
    box-shadow: 0 0 0 1px ${themeGet('colors.border.focused')};
    &[aria-invalid] {
      border-color: ${themeGet('colors.border.danger')};
      box-shadow: 0 0 0 1px ${themeGet('colors.border.danger')};
    }
  }
`
export const StyledActionsFilterButton = styled(Combobox.Button)`
  // reset
  appearance: none;
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  vertical-align: middle;
  cursor: pointer;
  text-align: left;
  font: inherit;
  user-select: none;
  align-items: center;
  color: inherit;

  // button placement
  margin-left: -2.48rem;

  // Fuegokit default theme styles
  font-weight: normal;
  font-size: ${themeGet('fontSizes.3')};
  text-decoration: none;
  opacity: 0.85;
  font-weight: ${themeGet('fontWeights.medium')};
  padding: ${themeGet('space.2')} 8px;
  border-radius: ${themeGet('radii.1')};

  &:hover,
  &:focus {
    opacity: 1;
    text-decoration: none;
    /* color: ${themeGet('colors.link.default')}; */
    /* background-color: ${themeGet('colors.background.neutral.subtle.hovered')}; */
  }
  &:active {
    opacity: 1;
    text-decoration: none;
    background-color: ${themeGet('colors.background.selected.default.[default]')};
    color: ${themeGet('colors.text.selected')};
  }
  &:focus,
  .focus-visible {
    outline: none;
    transition-duration: 0s, 0.2s;
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  &:focus:not(:focus-visible):not(.focus-visible) {
    box-shadow: 0 0 0 2px ${themeGet('colors.border.focused')};
  }
  svg {
    transition: transform 0.15s ease;
  }
  &[aria-expanded='true'] {
    --icon-rotation: 180deg;
    /* background-color: ${themeGet('colors.background.selected.default.[default]')}; */
    color: ${themeGet('colors.text.selected')};
    svg {
      transform: rotate(var(--icon-rotation));
    }
  }
`
export const StyledActionsFilterHeader = styled(Combobox.Header)`
  font-size: ${themeGet('fontSizes.4')};
  font-weight: ${themeGet('fontWeights.medium')};
  padding: ${themeGet('space.2')} ${themeGet('space.3')};
`
export const StyledActionsFilterSection = styled(Combobox.Section)`
  &:not(:first-child) {
    margin-top: 12px;
  }
`
export const StyledActionsFilterText = styled(Combobox.Text)`
  &[slot='label'] {
    font-size: ${themeGet('fontSizes.3')};
    font-weight: ${themeGet('fontWeights.semibold')};
  }
  &[slot='errorMessage'] {
    font-size: ${themeGet('fontSizes.1')};
    color: var(--ds-text-danger);
  }
  &[slot='description'] {
    font-size: ${themeGet('fontSizes.1')};
  }
  &[slot='description'] {
    &[aria-selected='true'] {
      color: ${themeGet('colors.text.selected')};
    }
  }
`
export const StyledActionsFilterLabel = styled(Combobox.Label)`
  &[slot='label'] {
  }
`
export const StyledActionsFilterSeparator = styled(Box)`
  height: 1px;
  background-color: ${themeGet('colors.border.default')};
  margin: ${themeGet('space.2')};
`

// Complex Combobox Item
interface ComplexComboboxItemProps extends ComboboxItemProps {
  /**
   * A visual that renders inside the Rule Item before the title and description
   */
  leadingVisual?: string | React.ComponentType<React.PropsWithChildren<{className?: string}>>
  /**
   * Only to be used as a last resort
   */
  UNSAFE_className?: string
  /**
   * Content to render in the combobox item
   */
  children?: React.ReactNode
  /**
   *
   */
  description?: string | React.ReactNode
  /**
   *
   */
  label?: string | React.ReactNode
}
export const ComplexComboboxItem = ({
  UNSAFE_className,
  leadingVisual: LeadingVisual,
  children,
  label,
  description,
  ...props
}: ComplexComboboxItemProps) => {
  return (
    <Combobox.Item className={UNSAFE_className} {...props}>
      <InnerVisualSlot>{typeof LeadingVisual === 'function' ? <LeadingVisual /> : LeadingVisual}</InnerVisualSlot>
      {children}
      {label && description ? (
        <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <StyledActionsFilterText slot="label">{label}</StyledActionsFilterText>
          <StyledActionsFilterText slot="description">{description}</StyledActionsFilterText>
        </Box>
      ) : null}
    </Combobox.Item>
  )
}
// InnerVisualSlot
interface InnerVisualSlotProps {
  /** */
  children?: React.ReactNode
}
/**
 * Inner visual slot to house leading visual, trailing visual, counter, and loading spinners
 */
export const InnerVisualSlot = ({children}: InnerVisualSlotProps) => {
  // just render a visual
  return (
    <span className="combobox-item-visual">
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2}}>{children}</Box>
    </span>
  )
}
export const StyledComplexActionsFilterItem = styled(ComplexComboboxItem)<ComplexComboboxItemProps>`
  padding: ${themeGet('space.2')} 0;
  /* border-radius: ${themeGet('radii.1')}; */
  outline: none;
  cursor: default;
  color: ${themeGet('colors.text.default')};
  font-size: ${themeGet('fontSizes.3')};
  position: relative;
  width: 100%;
  padding: 6px 12px 6px 16px;
  display: flex;
  align-items: center;
  gap: 4px;
  &:focus {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &:hover {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[data-focus-visible] {
    background: ${themeGet('colors.background.neutral.subtle.hovered')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-selected='true'] {
    background-color: ${themeGet('colors.background.selected.default.hovered')};
    color: ${themeGet('colors.text.selected')};
    font-weight: 600;
    //
    box-shadow: inset 2px 0px 0px ${themeGet('colors.border.selected')};
    &:hover {
      background: ${themeGet('colors.background.neutral.subtle.hovered')};
      color: ${themeGet('colors.text.default')};
    }
    &::before {
      /* content: '✓';
      content: '✓' / ''; */
      alt: ' ';
      position: absolute;
      top: 6px;
      left: 2px;
    }
  }
  &[data-pressed] {
    background-color: ${themeGet('colors.background.neutral.subtle.pressed')};
    color: ${themeGet('colors.text.default')};
  }
  &[aria-disabled] {
    background-color: ${themeGet('colors.elevation.surface.default.[default]')};
    color: ${themeGet('colors.text.disabled')};
    cursor: not-allowed;
  }

  @media (forced-colors: active) {
    &:focus {
      background: Highlight;
      color: HighlightText;
    }
  }
`
